import { useEffect, useRef, useState } from 'react';
import {
  View, Text, FlatList, TextInput, TouchableOpacity,
  StyleSheet, ActivityIndicator, Image, KeyboardAvoidingView, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useAuth } from '@/hooks/useAuth';
import { useStudentConversation } from '@/hooks/useStudentConversation';
import { useMessages, markConversationRead } from '@/hooks/useMessages';
import { useSendMessage } from '@/hooks/useSendMessage';
import { Message } from '@/lib/types';

export default function StudentChatScreen() {
  const { profile } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { conversationId, isLoading: convLoading } = useStudentConversation(profile?.id ?? '');
  const { messages, isLoading: msgsLoading } = useMessages(conversationId ?? '');
  const { sendText, sendPhoto, isSending } = useSendMessage(
    conversationId ?? '',
    profile?.id ?? ''
  );
  const [inputText, setInputText] = useState('');
  const listRef = useRef<FlatList>(null);

  useEffect(() => {
    if (conversationId && profile?.id) {
      markConversationRead(conversationId, 'student').then(() => {
        queryClient.invalidateQueries({ queryKey: ['student-conversation'] });
      });
    }
  }, [conversationId, profile?.id, queryClient]);

  useEffect(() => {
    if (messages.length > 0) {
      listRef.current?.scrollToEnd({ animated: false });
    }
  }, [messages.length]);

  async function handleSendText() {
    const text = inputText;
    setInputText('');
    await sendText(text);
    listRef.current?.scrollToEnd({ animated: true });
  }

  function renderMessage({ item }: { item: Message }) {
    const isOwn = item.sender_id === profile?.id;
    return (
      <View style={[styles.messageRow, isOwn ? styles.messageRowRight : styles.messageRowLeft]}>
        <View style={[styles.bubble, isOwn ? styles.bubbleOwn : styles.bubbleOther]}>
          {item.photo_url ? (
            <Image source={{ uri: item.photo_url }} style={styles.photoMessage} resizeMode="cover" />
          ) : (
            <Text style={styles.messageText}>{item.content}</Text>
          )}
          <Text style={[styles.messageTime, isOwn ? styles.messageTimeOwn : styles.messageTimeOther]}>
            {new Date(item.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </Text>
        </View>
      </View>
    );
  }

  if (convLoading || msgsLoading) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={88}
    >
      <View style={styles.headerBar}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color="#1A1A1A" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Chat com seu personal</Text>
      </View>
      <FlatList
        ref={listRef}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={messages}
        keyExtractor={(item) => item.id}
        renderItem={renderMessage}
        onContentSizeChange={() => listRef.current?.scrollToEnd({ animated: false })}
        ListEmptyComponent={
          <View style={styles.emptyWrap}>
            <Text style={styles.emptyText}>Nenhuma mensagem ainda.</Text>
            <Text style={styles.emptySubText}>Mande uma mensagem para seu personal!</Text>
          </View>
        }
      />
      <View style={styles.inputRow}>
        <TouchableOpacity onPress={sendPhoto} style={styles.photoButton} disabled={isSending}>
          <Ionicons name="image-outline" size={24} color="#C8102E" />
        </TouchableOpacity>
        <TextInput
          style={styles.input}
          value={inputText}
          onChangeText={setInputText}
          placeholder="Mensagem..."
          placeholderTextColor="#aaa"
          multiline
          maxLength={1000}
        />
        <TouchableOpacity
          onPress={handleSendText}
          style={[styles.sendButton, (!inputText.trim() || isSending) && styles.sendButtonDisabled]}
          disabled={!inputText.trim() || isSending}
        >
          <Ionicons name="send" size={18} color="#fff" />
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  headerBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 56,
    paddingBottom: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  backButton: { padding: 4 },
  headerTitle: { fontSize: 17, fontWeight: '700', color: '#1A1A1A' },
  list: { flex: 1 },
  listContent: { padding: 16, paddingBottom: 8 },
  emptyWrap: { alignItems: 'center', paddingTop: 60 },
  emptyText: { fontSize: 15, color: '#888' },
  emptySubText: { fontSize: 13, color: '#aaa', marginTop: 6 },
  messageRow: { marginBottom: 8 },
  messageRowRight: { alignItems: 'flex-end' },
  messageRowLeft: { alignItems: 'flex-start' },
  bubble: { maxWidth: '78%', borderRadius: 16, padding: 10 },
  bubbleOwn: { backgroundColor: '#FFF0F2', borderBottomRightRadius: 4 },
  bubbleOther: { backgroundColor: '#fff', borderBottomLeftRadius: 4 },
  messageText: { fontSize: 15, lineHeight: 20, color: '#1A1A1A' },
  messageTime: { fontSize: 10, marginTop: 4 },
  messageTimeOwn: { color: '#C8102E', textAlign: 'right' },
  messageTimeOther: { color: '#aaa' },
  photoMessage: { width: 200, height: 200, borderRadius: 8 },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  photoButton: { padding: 6 },
  input: {
    flex: 1,
    backgroundColor: '#F5F5F5',
    borderRadius: 20,
    paddingHorizontal: 14,
    paddingVertical: 8,
    fontSize: 15,
    color: '#1A1A1A',
    maxHeight: 100,
  },
  sendButton: {
    backgroundColor: '#C8102E',
    borderRadius: 20,
    width: 36,
    height: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonDisabled: { backgroundColor: '#ddd' },
});
